import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { useQuery } from "convex/react";
import { useObservedMutation } from "@/hooks/use-observed-convex";

/**
 * Per-consultation projections for the clarification timeline (LAT-626): a
 * collapsed 5-state `displayStatus`, reply/transcript counts, the Teams deep
 * link, and the summary message id — everything the timeline needs to render
 * without re-deriving status or counting replies on the client.
 */
export function useConsultations(clarificationRequestId: Id<"clarificationRequests"> | null) {
  return useQuery(
    api.clarificationConsultations.api.listProjectionsByClarification,
    clarificationRequestId ? { clarificationRequestId } : "skip"
  );
}

export function useStartTeamsConsultation() {
  return useObservedMutation(api.clarificationConsultations.api.startTeamsConsultation, {
    feature: "clarification-consultations",
    operation: "start-teams-consultation",
    includeRequestId: true,
  });
}

export function useRetryConsultationSetup() {
  return useObservedMutation(api.clarificationConsultations.api.retryConsultationSetup, {
    feature: "clarification-consultations",
    operation: "retry-consultation-setup",
    includeRequestId: true,
  });
}

export function useCancelConsultation() {
  return useObservedMutation(api.clarificationConsultations.api.cancelConsultation, {
    feature: "clarification-consultations",
    operation: "cancel-consultation",
    includeRequestId: true,
  });
}

export function useTeamsChatByConsultation(
  consultationId: Id<"clarificationConsultations"> | null
) {
  return useQuery(api.teams.chats.getByConsultation, consultationId ? { consultationId } : "skip");
}

export function useConsultationTranscript(teamsChatId: Id<"teamsChats"> | null) {
  return useQuery(api.teams.messages.listByChat, teamsChatId ? { teamsChatId } : "skip");
}
